import { FC } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { useIntl } from 'react-intl'

import Dashboard from 'layouts/Dashboard'
import { AdditionalServicesLease } from 'components/AdditionalServices'

const ROOT_PATH = '/additional-services'

// Layout for the Leases section ("Panel Klienta 3.0" redesign).
// Desktop: full-width list on the root path, full-width detail / subpages elsewhere.
// Mobile: contracts list on the root path, full-screen subpages elsewhere.
const LeasesLayout: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const hubMatch = matchPath({ path: `${ROOT_PATH}/:id`, end: true }, pathname)
  const nestedMatch = matchPath(`${ROOT_PATH}/:id/*`, pathname)
  const isInformation = !!matchPath(`${ROOT_PATH}/:id/information`, pathname)
  const isPaymentSchedule = !!matchPath(
    { path: `${ROOT_PATH}/:id/payment-schedule`, end: true },
    pathname
  )
  const isInvoicePreview = !!matchPath(
    `${ROOT_PATH}/:id/payment-schedule/:documentType/:documentId`,
    pathname
  )
  const isProtocol = !!matchPath({ path: `${ROOT_PATH}/:id/protocol`, end: true }, pathname)
  const isBookingAgreement = !!matchPath(`${ROOT_PATH}/:id/booking-agreement`, pathname)
  const isFullWidthSubpage =
    isInformation || isPaymentSchedule || isInvoicePreview || isProtocol || isBookingAgreement
  const isNestedSubpage = !!nestedMatch?.params['*'] && !isFullWidthSubpage && !hubMatch

  if (isDesktop) {
    return (
      <Dashboard noHeader disableGutters>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh' }}>
          <Box sx={{ maxWidth: '1200px', px: '36px', pt: '28px', pb: '64px' }}>
            {pathname === ROOT_PATH ? (
              <>
                <Typography
                  sx={{
                    fontSize: '24px',
                    fontWeight: 700,
                    lineHeight: '28.8px',
                    color: theme.r4r.navy,
                    mb: '16px'
                  }}>
                  {formatMessage({ id: 'additionalServicesLease.contracts', defaultMessage: 'Umowy' })}
                </Typography>
                <AdditionalServicesLease />
              </>
            ) : isNestedSubpage ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
                  <IconButton
                    onClick={() => navigate(`${ROOT_PATH}/${nestedMatch.params.id}`)}
                    sx={{ width: '36px', height: '36px', color: theme.r4r.navy, borderRadius: 0 }}>
                    <ArrowBack sx={{ fontSize: '22px' }} />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 700,
                      lineHeight: '28.8px',
                      color: theme.r4r.navy
                    }}>
                    {formatMessage({ id: 'common.goBack', defaultMessage: 'Wróć' })}
                  </Typography>
                </Box>
                <Box sx={{ backgroundColor: '#fff', boxShadow: theme.r4r.cardShadow, p: '28px' }}>
                  <Outlet />
                </Box>
              </>
            ) : (
              <Outlet />
            )}
          </Box>
        </Box>
      </Dashboard>
    )
  }

  if (pathname === ROOT_PATH) {
    return (
      <Dashboard noHeader>
        <Box sx={{ backgroundColor: theme.r4r.greyBg, minHeight: '100vh', pb: '24px' }}>
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              color: theme.r4r.navy,
              textAlign: 'center',
              pt: '18px',
              pb: '14px'
            }}>
            {formatMessage({ id: 'additionalServicesLease.contracts', defaultMessage: 'Umowy' })}
          </Typography>
          <Box sx={{ px: '16px' }}>
            <Outlet />
          </Box>
        </Box>
      </Dashboard>
    )
  }

  return <Outlet />
}

export default LeasesLayout
